import {
  getActivePromoCampaign,
  normalizePromoPath,
  PromoCampaign,
} from "./campaigns";

type PromoDocumentState = {
  /** Path of the full page load; in-app navigation never changes it. */
  initialPathname: string;
  hasEvaluatedThisDocument: boolean;
  campaign: PromoCampaign | null;
  /** Set once the promo has opened, been skipped, or been closed. */
  isConsumed: boolean;
};

const createState = (initialPathname: string): PromoDocumentState => ({
  initialPathname: normalizePromoPath(initialPathname),
  hasEvaluatedThisDocument: false,
  campaign: null,
  isConsumed: false,
});

/**
 * Lives at module scope so it survives React remounts (including the StrictMode double mount)
 * and resets only when the browser loads a new document.
 */
let documentState = createState(
  typeof window === "undefined" ? "/" : window.location.pathname,
);

export const getPromoInitialPathname = (): string =>
  documentState.initialPathname;

/**
 * Picks the campaign for this page load the first time it is called, then returns the same
 * answer until the promo is consumed.
 */
export const getPendingPromoCampaign = (now: number): PromoCampaign | null => {
  if (!documentState.hasEvaluatedThisDocument) {
    documentState.campaign = getActivePromoCampaign(
      documentState.initialPathname,
      now,
    );
    documentState.hasEvaluatedThisDocument = true;
  }
  return documentState.isConsumed ? null : documentState.campaign;
};

export const isPromoConsumed = (): boolean => documentState.isConsumed;

export const markPromoConsumed = (): void => {
  documentState.isConsumed = true;
};

/** Test-only: simulates a fresh full page load at `pathname`. */
export const resetPromoDocumentState = (
  pathname: string = typeof window === "undefined"
    ? "/"
    : window.location.pathname,
): void => {
  documentState = createState(pathname);
};
